/**
 * bn-zuletzt-gesehen.js — Web Component für theme/sections/bn-zuletzt-gesehen.liquid
 *
 * Liest und pflegt die Liste der zuletzt angesehenen Produkte in
 * localStorage. Auf Produktseiten rendert die Section ein JSON mit den
 * serverseitig aufbereiteten Daten des aktuellen Produkts (handle, url,
 * titel, bild, per money formatierter Preis) — die Komponente legt diesen
 * Eintrag vorn in die Liste (Duplikate nach handle entfernt, maximal
 * 8 Einträge, FIFO: der älteste fällt heraus) und rendert die übrigen
 * Einträge als Karten. Kein Netzwerk-Fetch: alles, was die Karte zeigt,
 * wurde beim damaligen Besuch gespeichert — der Preis ist bewusst der
 * Stand des Besuchs, kein Live-Preis.
 *
 * Regel 8 (CLAUDE.md): localStorage darf genutzt werden, aber alles muss
 * ohne funktionieren. Jeder Speicherzugriff steckt in try/catch — im
 * Safari-Privatmodus, bei abgelehnter Speicherung oder voller Quota bleibt
 * der Inhalt einfach hidden, ohne Konsolenfehler; die Seite bleibt intakt.
 * Es braucht dafür keinen Consent: rein funktionale First-Party-Speicherung
 * im Browser des Besuchers, keine Übertragung, kein Tracking.
 *
 * Karten entstehen über DOM-APIs (textContent, keine HTML-Strings) und nur
 * aus validierten Einträgen — Produkt-URLs müssen relativ sein, Bild-URLs
 * http(s) oder relativ. Manipulierte oder veraltete Speicherinhalte werden
 * still verworfen, nie ausgeführt.
 *
 * connectedCallback ist idempotent (kompletter Neu-Aufbau der Liste bei
 * jedem Connect, AbortController für Listener) — die Komponente übersteht
 * Morphs und Editor-Reorder. Im Theme-Editor rendert sie nach
 * shopify:section:load neu, bei bfcache-Rückkehr (pageshow) ebenfalls.
 *
 * bn-tokens.css holt die Komponente selbst (data-tokens-css) und erst dann,
 * wenn wirklich Karten entstehen: die Section steht auf jeder Seite, beim
 * ersten Besuch ist der Speicher aber leer und es erscheint garantiert
 * nichts. Ein stylesheet_tag im Liquid würde die Datei trotzdem laden.
 */

const SPEICHER_KEY = 'bn-zuletzt-gesehen';
const MAX_EINTRAEGE = 8;

/**
 * Bindet bn-tokens.css nach, sobald wirklich Karten erscheinen. Die Section
 * rendert auf jeder Seite, hat aber beim ersten Besuch garantiert keinen
 * Inhalt — ein stylesheet_tag im Liquid würde die Datei auch dann holen.
 * Ist der <link> schon da (anderer bn-Baustein auf derselben Seite), passiert
 * nichts. Die Regeln der Section tragen durchgehend Fallbackwerte, der Inhalt
 * darf also sofort sichtbar werden und springt beim Nachladen nicht.
 * @param {string} href
 */
function tokensSicherstellen(href) {
  if (!href) return;

  for (const vorhanden of document.querySelectorAll('link[rel="stylesheet"]')) {
    if (vorhanden.getAttribute('href') === href) return;
  }

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.append(link);
}

/**
 * @typedef {{ handle: string, url: string, titel: string, bild: string, preis: string }} Eintrag
 */

/**
 * @param {unknown} wert
 * @returns {wert is Eintrag}
 */
function istGueltigerEintrag(wert) {
  if (typeof wert !== 'object' || wert === null) return false;
  const eintrag = /** @type {Record<string, unknown>} */ (wert);
  return (
    typeof eintrag.handle === 'string' &&
    eintrag.handle !== '' &&
    typeof eintrag.url === 'string' &&
    eintrag.url.startsWith('/') &&
    typeof eintrag.titel === 'string' &&
    eintrag.titel !== '' &&
    typeof eintrag.preis === 'string' &&
    eintrag.preis !== '' &&
    typeof eintrag.bild === 'string' &&
    (eintrag.bild === '' ||
      eintrag.bild.startsWith('https://') ||
      eintrag.bild.startsWith('//') ||
      eintrag.bild.startsWith('/'))
  );
}

/**
 * Liest die gespeicherte Liste. Ohne localStorage (Privatmodus, abgelehnte
 * Speicherung), bei kaputtem JSON oder fremdem Format: leere Liste, kein Fehler.
 * @returns {Eintrag[]}
 */
function speicherLesen() {
  try {
    const roh = window.localStorage.getItem(SPEICHER_KEY);
    if (!roh) return [];
    const daten = JSON.parse(roh);
    if (!Array.isArray(daten)) return [];
    return daten.filter(istGueltigerEintrag).slice(0, MAX_EINTRAEGE);
  } catch {
    return [];
  }
}

/**
 * Schreibt die Liste. Schlägt das Speichern fehl, passiert bewusst nichts —
 * die Section funktioniert dann schlicht ohne Verlauf (Regel 8).
 * @param {Eintrag[]} eintraege
 */
function speicherSchreiben(eintraege) {
  try {
    window.localStorage.setItem(SPEICHER_KEY, JSON.stringify(eintraege));
  } catch {
    // Kein Speicher verfügbar — still weitermachen.
  }
}

class BnZuletztGesehen extends HTMLElement {
  /** @type {AbortController | null} */
  #listeners = null;

  connectedCallback() {
    this.#listeners?.abort();
    this.#listeners = new AbortController();

    // bfcache-Rückkehr: Verlauf kann sich seit dem Einfrieren geändert haben.
    window.addEventListener(
      'pageshow',
      (event) => {
        if (event.persisted) this.#rendern();
      },
      { signal: this.#listeners.signal }
    );

    // Theme-Editor: Section-Updates können gemorpht werden, ohne dass das
    // Element neu verbunden wird — danach die Karten neu aufbauen.
    if (window.Shopify?.designMode) {
      document.addEventListener(
        'shopify:section:load',
        (event) => {
          if (event.target instanceof Element && event.target.contains(this)) this.#rendern();
        },
        { signal: this.#listeners.signal }
      );
    }

    this.#rendern();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#listeners = null;
  }

  /**
   * Registriert das aktuelle Produkt (falls die Section auf einer
   * Produktseite steht) und baut die Karten komplett neu auf. Ohne
   * anzeigbare Einträge bleibt der Inhalt hidden — die Section ist dann
   * unsichtbar, es gibt keinen leeren Kasten.
   */
  #rendern() {
    const inhalt = this.querySelector('[data-bn-zg-inhalt]');
    const liste = this.querySelector('[data-bn-zg-liste]');
    if (!(inhalt instanceof HTMLElement) || !(liste instanceof HTMLElement)) return;

    const aktuell = this.#aktuellesProdukt();
    let eintraege = speicherLesen();

    if (aktuell) {
      eintraege = [aktuell, ...eintraege.filter((eintrag) => eintrag.handle !== aktuell.handle)];
      eintraege = eintraege.slice(0, MAX_EINTRAEGE);
      speicherSchreiben(eintraege);
    }

    const anzeigen = aktuell
      ? eintraege.filter((eintrag) => eintrag.handle !== aktuell.handle)
      : eintraege;

    if (anzeigen.length === 0) {
      liste.replaceChildren();
      inhalt.hidden = true;
      return;
    }

    tokensSicherstellen(this.dataset.tokensCss || '');
    liste.replaceChildren(...anzeigen.map((eintrag) => this.#karte(eintrag)));
    inhalt.hidden = false;
  }

  /**
   * Liest das serverseitig gerenderte JSON des aktuellen Produkts.
   * @returns {Eintrag | null}
   */
  #aktuellesProdukt() {
    const datenScript = this.querySelector('script[data-bn-zg-produkt]');
    if (!datenScript) return null;
    try {
      const daten = JSON.parse(datenScript.textContent ?? '');
      return istGueltigerEintrag(daten) ? daten : null;
    } catch {
      return null;
    }
  }

  /**
   * Baut eine Produktkarte aus einem validierten Eintrag. Ohne Bild bleibt
   * die 4:5-Fläche als Paper-200-Platzhalter stehen (Regel 11).
   * @param {Eintrag} eintrag
   * @returns {HTMLLIElement}
   */
  #karte(eintrag) {
    const punkt = document.createElement('li');
    punkt.className = 'bn-zg-karte';

    const link = document.createElement('a');
    link.className = 'bn-zg-link';
    link.href = eintrag.url;

    const flaeche = document.createElement('span');
    flaeche.className = 'bn-zg-bildflaeche';
    if (eintrag.bild !== '') {
      const bild = document.createElement('img');
      bild.className = 'bn-zg-bild';
      bild.src = eintrag.bild;
      bild.alt = '';
      bild.loading = 'lazy';
      bild.width = 280;
      bild.height = 350;
      flaeche.append(bild);
    }

    const name = document.createElement('span');
    name.className = 'bn-zg-name';
    name.textContent = eintrag.titel;

    const preis = document.createElement('span');
    preis.className = 'bn-zg-preis';
    preis.textContent = eintrag.preis;

    link.append(flaeche, name, preis);
    punkt.append(link);
    return punkt;
  }
}

if (!customElements.get('bn-zuletzt-gesehen')) {
  customElements.define('bn-zuletzt-gesehen', BnZuletztGesehen);
}
